import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Route, Routes, useLocation, useNavigate } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { MotionConfig } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { AppLoader } from '@/components/AppLoader';
import { AuthGate } from '@/components/AuthGate';
import { InstallPrompt } from '@/components/InstallPrompt';
import { OfflineBanner } from '@/components/OfflineBanner';
import { RootErrorBoundary } from '@/components/RootErrorBoundary';
import { RouteAnnouncer } from '@/components/RouteAnnouncer';
import { SkipToMain } from '@/components/SkipToMain';
import { UpdateToast } from '@/components/UpdateToast';
import { ENV } from '@/lib/env';
import { installKeyboardWatcher } from '@/lib/keyboard';
import { capturePageView } from '@/lib/posthog';
import { useScrollToTopOnRouteChange } from '@/lib/scrollRestoration';
import { refreshSession, refreshUserData } from '@/services/auth';
import { subscribeBroadcast } from '@/services/broadcast';
import { useAuthStore } from '@/stores/auth';
import { watchSystemTheme } from '@/stores/theme';

// Code-split per route — keeps the initial bundle within the §3.9 budget.
const PublicLayout = lazy(() =>
  import('@/routes/PublicLayout').then((m) => ({ default: m.PublicLayout })),
);
const WelcomePage = lazy(() =>
  import('@/routes/Welcome').then((m) => ({ default: m.WelcomePage })),
);
const SignInPage = lazy(() => import('@/routes/SignIn').then((m) => ({ default: m.SignInPage })));
const SignUpPage = lazy(() => import('@/routes/SignUp').then((m) => ({ default: m.SignUpPage })));
const PublicAuthLayout = lazy(() =>
  import('@/routes/PublicAuthLayout').then((m) => ({ default: m.PublicAuthLayout })),
);
const ForgotPasswordPage = lazy(() =>
  import('@/routes/ForgotPassword').then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import('@/routes/ResetPassword').then((m) => ({ default: m.ResetPasswordPage })),
);
const NotFoundPage = lazy(() =>
  import('@/routes/NotFound').then((m) => ({ default: m.NotFoundPage })),
);
const FamilyJoinPage = lazy(() =>
  import('@/routes/FamilyJoin').then((m) => ({ default: m.FamilyJoinPage })),
);

const AppLayout = lazy(() =>
  import('@/routes/AppLayout').then((m) => ({ default: m.AppLayout })),
);
const HomePage = lazy(() => import('@/routes/Home').then((m) => ({ default: m.HomePage })));
const ProfilePage = lazy(() =>
  import('@/routes/Profile').then((m) => ({ default: m.ProfilePage })),
);
const ChartCreatePage = lazy(() =>
  import('@/routes/chart/Create').then((m) => ({ default: m.ChartCreatePage })),
);
const ChartDetailPage = lazy(() =>
  import('@/routes/chart/Detail').then((m) => ({ default: m.ChartDetailPage })),
);

const FortunePage = lazy(() =>
  import('@/routes/Fortune').then((m) => ({ default: m.FortunePage })),
);
const CompatibilityPage = lazy(() =>
  import('@/routes/Compatibility').then((m) => ({ default: m.CompatibilityPage })),
);
const CompatibilityHistoryPage = lazy(() =>
  import('@/routes/CompatibilityHistory').then((m) => ({
    default: m.CompatibilityHistoryPage,
  })),
);
const CompatibilityDetailPage = lazy(() =>
  import('@/routes/CompatibilityDetail').then((m) => ({
    default: m.CompatibilityDetailPage,
  })),
);
const FortuneOverviewPage = lazy(() =>
  import('@/routes/fortune/Overview').then((m) => ({ default: m.FortuneOverviewPage })),
);
const FortuneHourlyPage = lazy(() =>
  import('@/routes/fortune/Hourly').then((m) => ({ default: m.FortuneHourlyPage })),
);
const FortuneAuspiciousInfoPage = lazy(() =>
  import('@/routes/fortune/AuspiciousInfo').then((m) => ({
    default: m.FortuneAuspiciousInfoPage,
  })),
);
const AllDatesPage = lazy(() =>
  import('@/routes/auspicious/AllDates').then((m) => ({ default: m.AllDatesPage })),
);
const AuspiciousDateDetailPage = lazy(() =>
  import('@/routes/auspicious/DateDetail').then((m) => ({
    default: m.AuspiciousDateDetailPage,
  })),
);
const FeedbackPage = lazy(() =>
  import('@/routes/Feedback').then((m) => ({ default: m.FeedbackPage })),
);
const ShareCardPage = lazy(() =>
  import('@/routes/ShareCard').then((m) => ({ default: m.ShareCardPage })),
);
const FamilyPage = lazy(() =>
  import('@/routes/Family').then((m) => ({ default: m.FamilyPage })),
);
const FamilyMemberPage = lazy(() =>
  import('@/routes/family/Member').then((m) => ({ default: m.FamilyMemberPage })),
);
const FamilyAddPage = lazy(() =>
  import('@/routes/family/Add').then((m) => ({ default: m.FamilyAddPage })),
);
const AuspiciousPage = lazy(() =>
  import('@/routes/Auspicious').then((m) => ({ default: m.AuspiciousPage })),
);
const ChatPage = lazy(() =>
  import('@/routes/Chat').then((m) => ({ default: m.ChatPage })),
);
const ChatHistoryPage = lazy(() =>
  import('@/routes/ChatHistory').then((m) => ({ default: m.ChatHistoryPage })),
);
const PaywallPage = lazy(() =>
  import('@/routes/Paywall').then((m) => ({ default: m.PaywallPage })),
);

const OnboardingLayout = lazy(() =>
  import('@/routes/onboarding/OnboardingLayout').then((m) => ({
    default: m.OnboardingLayout,
  })),
);
const OnboardingWelcomePage = lazy(() =>
  import('@/routes/onboarding/Welcome').then((m) => ({
    default: m.OnboardingWelcomePage,
  })),
);
const OnboardingBenefitsPage = lazy(() =>
  import('@/routes/onboarding/Benefits').then((m) => ({
    default: m.OnboardingBenefitsPage,
  })),
);
const OnboardingProfilePage = lazy(() =>
  import('@/routes/onboarding/Profile').then((m) => ({
    default: m.OnboardingProfilePage,
  })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function AppRoutes() {
  return (
    <Suspense fallback={<AppLoader />}>
      <Routes>
        {/* Unauthenticated */}
        <Route element={<PublicLayout />}>
          <Route path="/welcome" element={<WelcomePage />} />
          <Route path="/sign-in" element={<SignInPage />} />
          <Route path="/sign-up" element={<SignUpPage />} />
        </Route>

        {/* Password reset — deliberately OUTSIDE PublicLayout: its
            `requireGuest` gate would bounce someone signed in on one device who
            is resetting the password for another. */}
        <Route element={<PublicAuthLayout />}>
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
        </Route>

        {/* Onboarding (authenticated, no chart yet) */}
        <Route element={<OnboardingLayout />}>
          <Route path="/onboarding/welcome" element={<OnboardingWelcomePage />} />
          <Route path="/onboarding/benefits" element={<OnboardingBenefitsPage />} />
          <Route path="/onboarding/profile" element={<OnboardingProfilePage />} />
        </Route>

        {/* Chart create / edit — auth-gated but layout-less so the form can use the full screen */}
        <Route
          path="/chart/create"
          element={
            <AuthGate requireAuth>
              <ChartCreatePage />
            </AuthGate>
          }
        />
        <Route
          path="/chart/edit"
          element={
            <AuthGate requireAuth>
              <ChartCreatePage />
            </AuthGate>
          }
        />
        <Route
          path="/chart/me"
          element={
            <AuthGate requireAuth>
              <ChartDetailPage />
            </AuthGate>
          }
        />

        {/* Authenticated app shell with bottom-tabs / sidebar */}
        <Route element={<AppLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/feedback" element={<FeedbackPage />} />
          <Route path="/share-card" element={<ShareCardPage />} />
          <Route path="/fortune" element={<FortunePage />} />
          <Route path="/fortune/overview" element={<FortuneOverviewPage />} />
          <Route path="/fortune/hourly" element={<FortuneHourlyPage />} />
          <Route path="/fortune/auspicious-info" element={<FortuneAuspiciousInfoPage />} />
          <Route path="/compatibility" element={<CompatibilityPage />} />
          <Route path="/compatibility/history" element={<CompatibilityHistoryPage />} />
          <Route path="/compatibility/:id" element={<CompatibilityDetailPage />} />
          <Route path="/auspicious" element={<AuspiciousPage />} />
          <Route path="/auspicious/all" element={<AllDatesPage />} />
          {/* Must come after /auspicious/all so the literal wins the match. */}
          <Route path="/auspicious/:date" element={<AuspiciousDateDetailPage />} />
          <Route path="/family" element={<FamilyPage />} />
          <Route path="/family/add" element={<FamilyAddPage />} />
          {/* Keyed by FamilyMember id, not user id — see services/family.ts. */}
          <Route path="/family/:memberId" element={<FamilyMemberPage />} />
        </Route>

        {/* AI chat — full-screen route outside the tab shell (no bottom-nav). */}
        <Route
          path="/chat"
          element={
            <AuthGate requireAuth>
              <ChatPage />
            </AuthGate>
          }
        />
        <Route
          path="/chat/history"
          element={
            <AuthGate requireAuth>
              <ChatHistoryPage />
            </AuthGate>
          }
        />

        {/* Paywall — auth-gated so the URL isn't reachable by signed-out visitors. */}
        <Route
          path="/paywall"
          element={
            <AuthGate requireAuth>
              <PaywallPage />
            </AuthGate>
          }
        />

        {/* Public family invite landing (no auth) — deep-links into the app. */}
        <Route path="/family/join" element={<FamilyJoinPage />} />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

function AppLifecycle() {
  const status = useAuthStore((s) => s.status);
  const navigate = useNavigate();
  const location = useLocation();

  // Cold-start refresh — runs once.
  useEffect(() => {
    void refreshSession();
  }, []);

  // Refetch user-bound data when the tab becomes visible (mirrors the mobile
  // useSubscriptionRefresh pattern).
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void refreshUserData();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // Cross-tab sign-out / sign-in / subscription-invalidation.
  useEffect(() => {
    return subscribeBroadcast((event) => {
      switch (event.type) {
        case 'signout':
          useAuthStore.getState().clear();
          navigate('/welcome', { replace: true });
          break;
        case 'signin':
        case 'subscription:invalidate':
          void refreshUserData();
          break;
      }
    });
  }, [navigate]);

  // OS-level theme sync + iOS keyboard watcher.
  useEffect(() => {
    const offTheme = watchSystemTheme();
    const offKeyboard = installKeyboardWatcher();
    return () => {
      offTheme();
      offKeyboard();
    };
  }, []);

  // Capture SPA route changes for PostHog. Lives here (not at App root) so
  // it has the BrowserRouter context.
  useScrollToTopOnRouteChange();
  useEffect(() => {
    capturePageView(location.pathname);
  }, [location.pathname]);

  if (status === 'unknown') return <AppLoader />;
  return null;
}

export default function App() {
  const { i18n } = useTranslation();

  return (
    <RootErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <GoogleOAuthProvider clientId={ENV.googleClientId || 'missing'}>
          <MotionConfig reducedMotion="user">
            <BrowserRouter>
              <SkipToMain />
              <AppLifecycle />
              <RouteAnnouncer />
              <AppRoutes />
              <OfflineBanner />
              <InstallPrompt />
              <UpdateToast />
              {/* Keep i18next reachable as a side-effect import target */}
              <span aria-hidden="true" hidden lang={i18n.language} />
            </BrowserRouter>
          </MotionConfig>
        </GoogleOAuthProvider>
      </QueryClientProvider>
    </RootErrorBoundary>
  );
}
